import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, EyeOff, Bug, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

type WindowKey = "1h" | "24h" | "7d";
const WINDOW_HOURS: Record<WindowKey, number> = { "1h": 1, "24h": 24, "7d": 168 };

interface EventRow {
  event_type: string;
  group_id: string | null;
  occurred_at: string;
  payload: Record<string, any> | null;
}

interface GroupRow {
  id: string;
  name: string | null;
}

interface Aggregation {
  group_id: string | null;
  group_name: string;
  mimicry: number;
  notActive: number;
  errors: number;
  v18Count: number;
  v19Count: number;
  total: number;
}

const TRACKED_EVENTS = ["mimicry.fake_syra_detected", "group.not_active", "webhook_pipeline.error", "skill.matched"] as const;

export default function PipelineErrors() {
  const [windowKey, setWindowKey] = useState<WindowKey>("24h");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [versionFilter, setVersionFilter] = useState<"all" | "v18" | "v19">("all");
  const [events, setEvents] = useState<EventRow[]>([]);
  const [groups, setGroups] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    const sinceISO = new Date(Date.now() - WINDOW_HOURS[windowKey] * 3600 * 1000).toISOString();
    const { data: eventsData, error: eventsErr } = await supabase
      .from("cortex_events")
      .select("event_type, group_id, occurred_at, payload")
      .in("event_type", TRACKED_EVENTS as unknown as string[])
      .gte("occurred_at", sinceISO)
      .order("occurred_at", { ascending: false })
      .limit(5000);
    if (eventsErr) {
      console.error("[PipelineErrors] load events failed", eventsErr);
    }
    const rows = (eventsData ?? []) as EventRow[];
    setEvents(rows);

    const groupIds = Array.from(new Set(rows.map((r) => r.group_id).filter((v): v is string => !!v)));
    if (groupIds.length > 0) {
      const { data: groupsData } = await supabase
        .from("syra_groups")
        .select("id, name")
        .in("id", groupIds);
      const map: Record<string, string> = {};
      ((groupsData ?? []) as GroupRow[]).forEach((g) => {
        map[g.id] = g.name ?? g.id.slice(0, 8);
      });
      setGroups(map);
    } else {
      setGroups({});
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowKey]);

  const aggregations = useMemo<Aggregation[]>(() => {
    const map = new Map<string, Aggregation>();
    for (const ev of events) {
      const key = ev.group_id ?? "__null__";
      if (!map.has(key)) {
        map.set(key, {
          group_id: ev.group_id,
          group_name: ev.group_id ? groups[ev.group_id] ?? ev.group_id.slice(0, 8) : "(sem grupo)",
          mimicry: 0,
          notActive: 0,
          errors: 0,
          v18Count: 0,
          v19Count: 0,
          total: 0,
        });
      }
      const agg = map.get(key)!;
      if (ev.event_type === "mimicry.fake_syra_detected") agg.mimicry += 1;
      else if (ev.event_type === "group.not_active") agg.notActive += 1;
      else if (ev.event_type === "webhook_pipeline.error") agg.errors += 1;
      else if (ev.event_type === "skill.matched") agg.v19Count += 1;
      if (ev.event_type !== "skill.matched") agg.total += 1;
    }
    // v18 = turnos que não foram v19 (heurística leve)
    return Array.from(map.values())
      .map((a) => ({ ...a, v18Count: Math.max(a.total - a.v19Count, 0) }))
      .sort((a, b) => b.total - a.total);
  }, [events, groups]);

  const filtered = useMemo(() => {
    return aggregations.filter((a) => {
      if (groupFilter !== "all" && a.group_id !== groupFilter) return false;
      if (versionFilter === "v18" && a.v18Count === 0) return false;
      if (versionFilter === "v19" && a.v19Count === 0) return false;
      return true;
    });
  }, [aggregations, groupFilter, versionFilter]);

  const totals = useMemo(() => {
    let mimicry = 0, notActive = 0, errors = 0, v18 = 0, v19 = 0;
    for (const a of filtered) {
      mimicry += a.mimicry; notActive += a.notActive; errors += a.errors;
      v18 += a.v18Count; v19 += a.v19Count;
    }
    return { mimicry, notActive, errors, v18, v19 };
  }, [filtered]);

  const recentErrors = useMemo(
    () => events
      .filter((e) => e.event_type === "webhook_pipeline.error")
      .slice(0, 20),
    [events],
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Erros & Sinais do Pipeline</h1>
          <p className="text-muted-foreground mt-1">
            Mimicry detectada, grupos inativos e erros do webhook-pipeline — por grupo e versão (v18/v19).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={windowKey} onValueChange={(v) => setWindowKey(v as WindowKey)}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Última 1h</SelectItem>
              <SelectItem value="24h">Últimas 24h</SelectItem>
              <SelectItem value="7d">Últimos 7d</SelectItem>
            </SelectContent>
          </Select>
          <Select value={versionFilter} onValueChange={(v) => setVersionFilter(v as "all" | "v18" | "v19")}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas versões</SelectItem>
              <SelectItem value="v18">Apenas v18</SelectItem>
              <SelectItem value="v19">Apenas v19</SelectItem>
            </SelectContent>
          </Select>
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos grupos</SelectItem>
              {aggregations.map((a) => (
                <SelectItem key={a.group_id ?? "__null__"} value={a.group_id ?? "__null__"}>
                  {a.group_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Mimicry / Fake SYRA</CardTitle>
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totals.mimicry}</div>
            <p className="text-xs text-muted-foreground mt-1">Turnos onde humano imitou o bot</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Group Not Active</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totals.notActive}</div>
            <p className="text-xs text-muted-foreground mt-1">Pipeline abortado por grupo desativado</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Erros do Webhook-Pipeline</CardTitle>
            <Bug className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totals.errors}</div>
            <p className="text-xs text-muted-foreground mt-1">Exceções não tratadas</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Distribuição por versão</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-xs text-muted-foreground">v18 (legacy)</div>
              <div className="text-2xl font-bold">{totals.v18}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">v19 (skill-os authority)</div>
              <div className="text-2xl font-bold">{totals.v19}</div>
            </div>
            <div className="flex-1 min-w-[200px]">
              {totals.v18 + totals.v19 > 0 && (
                <div className="h-3 w-full rounded-full bg-muted overflow-hidden flex">
                  <div className="bg-primary h-full" style={{ width: `${(totals.v19 / (totals.v18 + totals.v19)) * 100}%` }} />
                  <div className="bg-secondary h-full" style={{ width: `${(totals.v18 / (totals.v18 + totals.v19)) * 100}%` }} />
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-1">
                {totals.v18 + totals.v19 > 0
                  ? `${Math.round((totals.v19 / (totals.v18 + totals.v19)) * 100)}% v19 · ${Math.round((totals.v18 / (totals.v18 + totals.v19)) * 100)}% v18`
                  : "Sem turnos no período"}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="by-group">
        <TabsList>
          <TabsTrigger value="by-group">Por grupo</TabsTrigger>
          <TabsTrigger value="recent-errors">Erros recentes</TabsTrigger>
        </TabsList>
        <TabsContent value="by-group" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Grupo</TableHead>
                    <TableHead className="text-right">Mimicry</TableHead>
                    <TableHead className="text-right">Inativo</TableHead>
                    <TableHead className="text-right">Erros</TableHead>
                    <TableHead className="text-right">v18</TableHead>
                    <TableHead className="text-right">v19</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        Nenhum evento no período selecionado.
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((a) => (
                    <TableRow key={a.group_id ?? "__null__"}>
                      <TableCell className="font-medium">{a.group_name}</TableCell>
                      <TableCell className="text-right">
                        {a.mimicry > 0 ? <Badge variant="secondary">{a.mimicry}</Badge> : <span className="text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {a.notActive > 0 ? <Badge variant="outline">{a.notActive}</Badge> : <span className="text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {a.errors > 0 ? <Badge variant="destructive">{a.errors}</Badge> : <span className="text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell className="text-right">{a.v18Count}</TableCell>
                      <TableCell className="text-right">{a.v19Count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="recent-errors" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quando</TableHead>
                    <TableHead>Grupo</TableHead>
                    <TableHead>Mensagem</TableHead>
                    <TableHead>Versão</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentErrors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        Sem erros no período.
                      </TableCell>
                    </TableRow>
                  )}
                  {recentErrors.map((e, i) => (
                    <TableRow key={`${e.occurred_at}-${i}`}>
                      <TableCell className="text-xs whitespace-nowrap">{new Date(e.occurred_at).toLocaleString("pt-BR")}</TableCell>
                      <TableCell className="text-sm">{e.group_id ? groups[e.group_id] ?? e.group_id.slice(0, 8) : "—"}</TableCell>
                      <TableCell className="text-xs font-mono max-w-[480px] truncate" title={e.payload?.error_message ?? ""}>
                        {e.payload?.error_message ?? "(sem mensagem)"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{e.payload?.pipeline_version ?? "unknown"}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
